import { motion } from "framer-motion";
import { Cog, ShieldCheck, Ship, Wrench } from "lucide-react";

const SERVICE_IMG = "/after-sales-detailing.jpg";

const SERVICES = [
  {
    icon: Wrench,
    no: "01",
    title: "Factory-Authorised Servicing",
    body: "Scheduled maintenance carried out by factory-trained technicians, following each shipyard's own service programmes and standards.",
  },
  {
    icon: ShieldCheck,
    no: "02",
    title: "Genuine Parts & Warranty",
    body: "OEM parts sourced directly from the manufacturer, with warranty registration, claims and documentation administered on your behalf.",
  },
  {
    icon: Cog,
    no: "03",
    title: "Engine & Systems Care",
    body: "Specialist attention for Volvo Penta, MAN, MTU, Rotax and electric drivetrains — plus electronics, hydraulics, generators and air-conditioning.",
  },
  {
    icon: Ship,
    no: "04",
    title: "Hull, Detailing & Seasonal Care",
    body: "Antifouling, gelcoat and paint correction, teak restoration, monsoon lay-up and pre-season recommissioning — so she is always ready to leave the berth.",
  },
];

export default function AfterSales() {
  return (
    <section id="after-sales" className="relative border-t border-navy-ink/5 bg-navy px-6 py-20 lg:px-12 lg:py-28" data-testid="after-sales-section">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          <div className="order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="sticky top-28"
            >
              <div className="overflow-hidden border border-navy-ink/10">
                <img
                  src={SERVICE_IMG}
                  alt="Technician detailing the teak deck and cleat of a luxury yacht"
                  className="aspect-[4/5] w-full object-cover max-h-[80vh]"
                  data-testid="after-sales-image"
                />
              </div>
              <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-mist" data-testid="after-sales-caption">
                Engineered to last. Maintained to prove it.
              </p>
            </motion.div>
          </div>

          <div className="order-1 lg:order-2">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="mb-6 text-[10px] uppercase tracking-[0.45em] text-gold sm:text-xs"
              data-testid="after-sales-overline"
            >
              04 — After Sales
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-navy-ink sm:text-5xl lg:text-6xl"
              data-testid="after-sales-heading"
            >
              Maintained to
              <span className="italic text-gold-light"> shipyard standard.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-6 max-w-md text-sm font-light leading-relaxed text-mist"
              data-testid="after-sales-description"
            >
              Every yacht we deliver stays under our care — serviced by factory-trained
              technicians, with genuine parts and one point of contact for its entire
              life with you.
            </motion.p>

            <div className="mt-16 space-y-2">
              {SERVICES.map((s, i) => (
                <motion.div
                  key={s.no}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: i * 0.1 }}
                  className="group flex gap-6 border-b border-navy-ink/10 py-8 transition-colors duration-500 hover:border-gold/30"
                  data-testid={`after-sales-service-${s.no}`}
                >
                  <s.icon size={22} strokeWidth={1} className="mt-1 shrink-0 text-gold transition-transform duration-500 group-hover:scale-110" />
                  <div>
                    <div className="flex items-baseline gap-4">
                      <span className="font-accent text-xs text-gold/60">{s.no}</span>
                      <h3 className="font-serif text-2xl font-medium text-navy-ink">{s.title}</h3>
                    </div>
                    <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-mist">{s.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
